import React, { useState, useEffect } from 'react';
import './CategorySelector.css';
import { categoryService } from '../../services/api';

const CategorySelector = ({
  onCategoryChange,
  selectedCategory,
  selectedSubcategory,
  onSubcategoryChange,
  selectedChildrenCategory,
  onChildrenCategoryChange,
  /** Called with main category display name when user picks a main category (or '' when cleared). */
  onMainCategoryDisplayName,
}) => {
  const [allCategories, setAllCategories] = useState([]); // All categories from API
  const [mainCategories, setMainCategories] = useState([]); // Main categories (no parent)
  const [subcategories, setSubcategories] = useState([]); // Subcategories of selected main category
  const [childrenCategories, setChildrenCategories] = useState([]); // Children categories of selected subcategory
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAllCategories();
  }, []);

  // If parent stored a sub/child id in `category`, remap to main + sub (+ child) once tree is loaded
  useEffect(() => {
    if (!selectedCategory || loading || allCategories.length === 0) return;
    const sel = String(selectedCategory).trim();
    const isMain = mainCategories.some((c) => String(c._id || c.id) === sel);
    if (isMain) return;

    const row = allCategories.find((c) => String(c._id || c.id) === sel);
    if (!row) return;

    const getParentId = (cat) => {
      if (cat.parentCategory) {
        if (typeof cat.parentCategory === 'object' && cat.parentCategory._id) {
          return cat.parentCategory._id;
        }
        return cat.parentCategory.id || cat.parentCategory;
      }
      if (cat.parent) {
        if (typeof cat.parent === 'object' && cat.parent._id) {
          return cat.parent._id;
        }
        return cat.parent.id || cat.parent;
      }
      return null;
    };

    const parentId = getParentId(row);
    if (!parentId) return;

    const parentRow = allCategories.find(
      (c) => String(c._id || c.id) === String(parentId).trim()
    );
    const grandParentId = parentRow ? getParentId(parentRow) : null;

    if (grandParentId) {
      onCategoryChange(String(grandParentId));
      onSubcategoryChange(String(parentId));
      if (onChildrenCategoryChange) onChildrenCategoryChange(sel);
    } else {
      onCategoryChange(String(parentId));
      onSubcategoryChange(sel);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, allCategories, mainCategories, loading]);

  // Load subcategories when main category is selected
  useEffect(() => {
    if (selectedCategory && allCategories.length > 0) {
      loadSubcategories(selectedCategory);
    } else {
      setSubcategories([]);
      setChildrenCategories([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, allCategories]);

  // Load children categories when subcategory is selected
  useEffect(() => {
    if (selectedSubcategory && allCategories.length > 0) {
      loadChildrenCategories(selectedSubcategory);
    } else {
      setChildrenCategories([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubcategory, allCategories]);

  const fetchAllCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔧 CategorySelector: Fetching all categories from API...');
      
      const response = await categoryService.getCategories();
      
      let categoriesData = [];
      
      if (response.success && response.categories) {
        if (Array.isArray(response.categories)) {
          categoriesData = response.categories;
        } else if (Array.isArray(response.categories.data)) {
          categoriesData = response.categories.data;
        }
      } else if (Array.isArray(response)) {
        categoriesData = response;
      }

      console.log('✅ CategorySelector: Total categories loaded:', categoriesData.length);
      
      setAllCategories(categoriesData);
      
      // Filter main categories (categories without parent)
      const mainCats = categoriesData.filter(cat => {
        const parentId = cat.parentCategory?._id || cat.parentCategory || cat.parent;
        return !parentId || parentId === '' || parentId === null;
      });
      
      console.log('✅ CategorySelector: Main categories found:', mainCats.length);
      setMainCategories(mainCats);
      
    } catch (error) {
      console.error('❌ CategorySelector: Error fetching categories:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSubcategories = (mainCategoryId) => {
    console.log('🔧 CategorySelector: Loading subcategories for main category:', mainCategoryId);
    
    // Helper function to extract parent ID
    const getParentId = (cat) => {
      if (cat.parentCategory) {
        if (typeof cat.parentCategory === 'object' && cat.parentCategory._id) {
          return cat.parentCategory._id;
        } else if (typeof cat.parentCategory === 'object') {
          return cat.parentCategory.id || cat.parentCategory;
        } else {
          return cat.parentCategory;
        }
      } else if (cat.parent) {
        if (typeof cat.parent === 'object' && cat.parent._id) {
          return cat.parent._id;
        } else if (typeof cat.parent === 'object') {
          return cat.parent.id || cat.parent;
        } else {
          return cat.parent;
        }
      }
      return null;
    };

    const mainIdString = String(mainCategoryId).trim();
    
    // Filter subcategories (categories whose parent is the selected main category)
    const subcats = allCategories.filter(cat => {
      const parentId = getParentId(cat);
      if (!parentId) return false;
      const parentIdString = String(parentId).trim();
      return parentIdString === mainIdString;
    });
    
    console.log('✅ CategorySelector: Subcategories found:', subcats.length);
    setSubcategories(subcats);
    
    // Reset children categories when subcategory changes
    setChildrenCategories([]);
    if (onChildrenCategoryChange) {
      onChildrenCategoryChange('');
    }
  };

  const loadChildrenCategories = (subcategoryId) => {
    console.log('🔧 CategorySelector: Loading children categories for subcategory:', subcategoryId);
    
    // Helper function to extract parent ID
    const getParentId = (cat) => {
      if (cat.parentCategory) {
        if (typeof cat.parentCategory === 'object' && cat.parentCategory._id) {
          return cat.parentCategory._id;
        } else if (typeof cat.parentCategory === 'object') {
          return cat.parentCategory.id || cat.parentCategory;
        } else {
          return cat.parentCategory;
        }
      } else if (cat.parent) {
        if (typeof cat.parent === 'object' && cat.parent._id) {
          return cat.parent._id;
        } else if (typeof cat.parent === 'object') {
          return cat.parent.id || cat.parent;
        } else {
          return cat.parent;
        }
      }
      return null;
    };

    const subIdString = String(subcategoryId).trim();
    
    // Filter children categories (categories whose parent is the selected subcategory)
    const children = allCategories.filter(cat => {
      const parentId = getParentId(cat);
      if (!parentId) return false;
      const parentIdString = String(parentId).trim();
      return parentIdString === subIdString;
    });
    
    console.log('✅ CategorySelector: Children categories found:', children.length);
    setChildrenCategories(children);
  };

  const handleMainCategoryChange = (categoryId) => {
    console.log('🔄 CategorySelector: Main category changed:', categoryId);
    onCategoryChange(categoryId);
    onSubcategoryChange(''); // Reset subcategory when main category changes
    if (onChildrenCategoryChange) {
      onChildrenCategoryChange(''); // Reset children category
    }
    if (onMainCategoryDisplayName) {
      if (!categoryId) {
        onMainCategoryDisplayName('');
      } else {
        const cat = mainCategories.find(
          (c) => String(c._id || c.id) === String(categoryId)
        );
        onMainCategoryDisplayName(cat?.name ? String(cat.name) : '');
      }
    }
  };

  const handleSubcategoryChange = (subcategoryId) => {
    console.log('🔄 CategorySelector: Subcategory changed:', subcategoryId);
    onSubcategoryChange(subcategoryId);
    if (onChildrenCategoryChange) {
      onChildrenCategoryChange(''); // Reset children category when subcategory changes
    }
  };

  if (loading) {
    return (
      <div className="category-selector-loading">
        <div className="spinner"></div>
        <span>Loading categories...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="category-selector-error">
        <div className="error-content">
          <i className="fas fa-exclamation-triangle"></i>
          <p>Error: {error}</p>
          <button onClick={fetchAllCategories} className="retry-btn">
            <i className="fas fa-redo"></i>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="category-selector">
      {/* Main Category Selection */}
      <div className="form-group">
        <label htmlFor="main-category">Main Category</label>
        <select
          id="main-category"
          value={selectedCategory ?? ''}
          onChange={(e) => handleMainCategoryChange(e.target.value)}
        >
          <option value="">Select Main Category</option>
          {mainCategories.map(category => (
            <option key={category._id || category.id} value={category._id || category.id}>
              {category.icon || '📁'} {category.name}
            </option>
          ))}
        </select>
        {selectedCategory && (
          <div className="category-info">
            <span className="category-description">
              {mainCategories.find(cat => (cat._id || cat.id) === selectedCategory)?.description || ''}
            </span>
          </div>
        )}
      </div>

      {/* Subcategory Selection - Shows when main category is selected */}
      {selectedCategory && subcategories.length > 0 && (
        <div className="form-group">
          <label htmlFor="subcategory">Subcategory</label>
          <select
            id="subcategory"
            value={selectedSubcategory ?? ''}
            onChange={(e) => handleSubcategoryChange(e.target.value)}
          >
            <option value="">Select Subcategory (Optional)</option>
            {subcategories.map(subcategory => (
              <option key={subcategory._id || subcategory.id} value={subcategory._id || subcategory.id}>
                {subcategory.name}
              </option>
            ))}
          </select>
          {selectedSubcategory && (
            <div className="subcategory-info">
              <span className="subcategory-description">
                {subcategories.find(sub => (sub._id || sub.id) === selectedSubcategory)?.description || ''}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Children Category Selection - Shows when subcategory is selected */}
      {selectedSubcategory && childrenCategories.length > 0 && (
        <div className="form-group">
          <label htmlFor="children-category">Children Category</label>
          <select
            id="children-category"
            value={selectedChildrenCategory || ''}
            onChange={(e) => onChildrenCategoryChange && onChildrenCategoryChange(e.target.value)}
          >
            <option value="">Select Children Category (Optional)</option>
            {childrenCategories.map(childrenCat => (
              <option key={childrenCat._id || childrenCat.id} value={childrenCat._id || childrenCat.id}>
                {childrenCat.name}
              </option>
            ))}
          </select>
          {selectedChildrenCategory && (
            <div className="children-category-info">
              <span className="children-category-description">
                {childrenCategories.find(child => (child._id || child.id) === selectedChildrenCategory)?.description || ''}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Selection Summary */}
      {(selectedCategory || selectedSubcategory || selectedChildrenCategory) && (
        <div className="selection-summary">
          {selectedCategory && (
            <div className="summary-item">
              <strong>Main Category:</strong> {mainCategories.find(cat => (cat._id || cat.id) === selectedCategory)?.name || 'N/A'}
            </div>
          )}
          {selectedSubcategory && (
            <div className="summary-item">
              <strong>Subcategory:</strong> {subcategories.find(sub => (sub._id || sub.id) === selectedSubcategory)?.name || 'N/A'}
            </div>
          )}
          {selectedChildrenCategory && (
            <div className="summary-item">
              <strong>Children Category:</strong> {childrenCategories.find(child => (child._id || child.id) === selectedChildrenCategory)?.name || 'N/A'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CategorySelector;
